import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ReviewListParams, ReviewStatusFilter } from '@/types/review';

export interface ReviewQueryState {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  productId: string;
  customerId: string;
  status: ReviewStatusFilter;
  rating: number | 'all';
}

type ParamPatch = Partial<{
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  productId: string | null;
  customerId: string | null;
  status: ReviewStatusFilter | null;
  rating: number | 'all' | null;
}>;

const DEFAULTS: ReviewQueryState = {
  page: 1,
  limit: 10,
  sortBy: 'createdAt',
  sortOrder: 'desc',
  productId: '',
  customerId: '',
  status: 'all',
  rating: 'all',
};

function parseStatus(value: string | null): ReviewStatusFilter {
  if (value === 'pending' || value === 'approved' || value === 'rejected') return value;
  return 'all';
}

function parseRating(value: string | null): number | 'all' {
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 5) return parsed;
  return 'all';
}

export function useReviewQueryParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<ReviewQueryState>(() => {
    const page = Number(searchParams.get('page') || DEFAULTS.page);
    const limit = Number(searchParams.get('limit') || DEFAULTS.limit);

    return {
      page: Number.isFinite(page) && page > 0 ? page : DEFAULTS.page,
      limit: Number.isFinite(limit) && limit > 0 ? Math.min(limit, 100) : DEFAULTS.limit,
      sortBy: searchParams.get('sortBy') || DEFAULTS.sortBy,
      sortOrder: searchParams.get('sortOrder') === 'asc' ? 'asc' : 'desc',
      productId: searchParams.get('productId') || '',
      customerId: searchParams.get('customerId') || '',
      status: parseStatus(searchParams.get('status')),
      rating: parseRating(searchParams.get('rating')),
    };
  }, [searchParams]);

  const apiParams = useMemo<ReviewListParams>(
    () => ({
      page: state.page,
      limit: state.limit,
      sortBy: state.sortBy,
      sortOrder: state.sortOrder,
      ...(state.productId ? { productId: state.productId } : {}),
      ...(state.customerId ? { customerId: state.customerId } : {}),
      ...(state.status !== 'all' ? { status: state.status } : {}),
      ...(state.rating !== 'all' ? { rating: state.rating } : {}),
    }),
    [state],
  );

  const patchParams = useCallback(
    (patch: ParamPatch, options?: { resetPage?: boolean }) => {
      setSearchParams(
        (searchParams) => {
          const next = new URLSearchParams(searchParams);
          const shouldResetPage = options?.resetPage ?? false;

          if (shouldResetPage) {
            next.set('page', '1');
          }

          const apply = (
            key: string,
            value: string | null | undefined,
            omitValues: string[] = [],
          ) => {
            if (value === null || value === undefined || value === '' || omitValues.includes(value)) {
              next.delete(key);
              return;
            }
            next.set(key, value);
          };

          if (patch.page !== undefined) next.set('page', String(patch.page));
          if (patch.limit !== undefined) next.set('limit', String(patch.limit));
          if (patch.sortBy !== undefined) next.set('sortBy', patch.sortBy);
          if (patch.sortOrder !== undefined) next.set('sortOrder', patch.sortOrder);
          if (patch.productId !== undefined) apply('productId', patch.productId);
          if (patch.customerId !== undefined) apply('customerId', patch.customerId);
          if (patch.status !== undefined) apply('status', patch.status, ['all']);
          if (patch.rating !== undefined) {
            apply('rating', patch.rating === 'all' ? null : String(patch.rating), ['all']);
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const resetAll = useCallback(() => {
    setSearchParams(
      (searchParams) => {
        const next = new URLSearchParams(searchParams);
        next.delete('productId');
        next.delete('customerId');
        next.delete('status');
        next.delete('rating');
        next.delete('sortBy');
        next.delete('sortOrder');
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const hasActiveFiltersOrSort = useMemo(
    () =>
      state.productId !== '' ||
      state.customerId !== '' ||
      state.status !== 'all' ||
      state.rating !== 'all' ||
      state.sortBy !== DEFAULTS.sortBy ||
      state.sortOrder !== DEFAULTS.sortOrder,
    [state],
  );

  return { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort };
}
